import { useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { PropertyKind } from "@interfaces/properties.interface";
import {
  searchPropertiesByKeyword,
  SearchSuggestItem,
} from "@services/krent.service";
import { addPageToSessionStorage, getSearchResultListings } from "@utils/helpers";
import { useSearchSuggestions } from "../../../hooks/useSearchSuggestions";
import { SearchSuggestDropdown } from "./SearchSuggestDropdown";
import CustomDatePicker from "./DatePicker";
import "../../../search.css";

type Props = {
  location?: string;
  propertySize?: string;
  price?: string;
  propertyTypes?: PropertyKind[];
  status?: string;
  intent?: string | null;
  maxPprice?: any;
  extraClass?: string;
  setPropertyIntent?: any;
  sizeClass: string;
  page?: number;
  setPage?: (param: number) => void;
  limit?: number;
};

export const SearchCard2: React.FC<Props> = ({
  extraClass,
  setPage,
  limit = 12,
}: Props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isTourMode = searchParams.get("tour") === "true";

  const withTour = (base: string) =>
    isTourMode ? `${base}${base.includes("?") ? "&" : "?"}tour=true` : base;

  const [keyWord, setKeyWord] = useState<string>("");
  const [keyWordLoading, setKeyWordLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const prevKeyWord = useRef<string>("");
  const [checkInDate, setCheckInDate] = useState<Date | null>(null);
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(null);
  const {
    items,
    open,
    setOpen,
    activeIndex,
    setActiveIndex,
    wrapRef: suggestWrapRef,
    onKeyDown: onSuggestKeyDown,
  } = useSearchSuggestions(keyWord);

  const onSearchKeyWord = async (override?: string) => {
    const query = (override ?? keyWord).trim();
    if (!query && !checkInDate && !checkOutDate) {
      navigate(withTour("/properties/"));
      return;
    }

    setKeyWordLoading(true);
    setOpen(false);
    try {
      const startDate = checkInDate
        ? checkInDate.toISOString().split("T")[0]
        : undefined;
      const endDate = checkOutDate
        ? checkOutDate.toISOString().split("T")[0]
        : undefined;

      const result = await searchPropertiesByKeyword(
        query,
        startDate || "",
        endDate || "",
        1,
        isTourMode ? { tour: "true" } : undefined,
        limit,
      );

      if (getSearchResultListings(result).length > 0) {
        setError("");
        if (setPage) setPage(1);
        addPageToSessionStorage(1);
        navigate(withTour("/properties/"), {
          state: {
            searchResult: result,
            keyWord: query,
            checkInDate,
            checkOutDate,
          },
        });
      } else {
        setError("Your search returned no matches. Try a different location.");
      }
    } catch (error) {
      console.error("Error during search:", error);
      setError(
        "An error occurred while processing your request. Please try again later.",
      );
    } finally {
      setKeyWordLoading(false);
    }
  };

  const selectSuggestion = (item: SearchSuggestItem) => {
    setKeyWord(item.query);
    onSearchKeyWord(item.query);
  };

  const handleFilter = () => {
    if (window.location.pathname !== "/") {
      navigate(withTour("/properties/"));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyWord(e.target.value);
    setError("");
  };

  const handleInputClear = () => {
    if (window.location.pathname !== "/")
      navigate(
        window.location.pathname.includes("properties")
          ? withTour("/explore/")
          : withTour("/properties/"),
      );
  };

  return (
    <>
      <div className="search-container">
        <div className="search-card px-3">
          <div className={`search-row ${extraClass}`}>
            <div className="search-main">
              {/* Location input */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  onSearchKeyWord();
                }}
                className="w-100"
              >
                <div className="search-suggest-wrap" ref={suggestWrapRef}>
                  <div className="search-input-wrapper">
                    <span className="search-icon">
                      <i className="fa fa-search"></i>
                    </span>

                    <input
                      className="search-input"
                      type="text"
                      value={keyWord}
                      autoComplete="off"
                      role="combobox"
                      aria-expanded={open}
                      aria-autocomplete="list"
                      onChange={handleInputChange}
                      onKeyDown={(event) => {
                        const picked = onSuggestKeyDown(event);
                        if (picked && typeof picked === "object") {
                          selectSuggestion(picked);
                        }
                      }}
                      onFocus={() => items.length > 0 && setOpen(true)}
                      onInput={(e: any) => {
                        const currentKeyWord = e.target.value;
                        if (prevKeyWord.current && currentKeyWord === "") {
                          handleInputClear();
                        }
                        prevKeyWord.current = currentKeyWord;
                      }}
                      placeholder="Location, Title, Ref.No"
                    />

                    {window.location.pathname !== "/" && keyWord && (
                      <Link
                        className="search-clear"
                        to={
                          window.location.pathname.includes("properties")
                            ? withTour("/explore/")
                            : withTour("/properties")
                        }
                        onClick={() => setKeyWord("")}
                      >
                        <i className="fa fa-x"></i>
                      </Link>
                    )}
                  </div>
                  <SearchSuggestDropdown
                    items={items}
                    open={open}
                    activeIndex={activeIndex}
                    onHover={setActiveIndex}
                    onSelect={selectSuggestion}
                  />
                </div>
              </form>

              {/* Date picker */}
              <CustomDatePicker
                checkInDate={checkInDate}
                setCheckInDate={setCheckInDate}
                checkOutDate={checkOutDate}
                setCheckOutDate={setCheckOutDate}
              />
            </div>

            {/* Action buttons — icon only, white icons on teal */}
            <div className="search-actions">
              <button
                className="search-filter-btn"
                data-bs-toggle="offcanvas"
                data-bs-target="#offcanvasExample"
                onClick={handleFilter}
                title="Filters"
              >
                <i className="fas fa-sliders text-white"></i>
              </button>

              <button
                className="search-submit-btn"
                onClick={() => {
                  addPageToSessionStorage(1);
                  onSearchKeyWord();
                }}
                title="Search"
                disabled={keyWordLoading}
              >
                {keyWordLoading ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <i className="fas fa-search text-white"></i>
                )}
              </button>
            </div>
          </div>

          {error && <p className="search-error">{error}</p>}
        </div>
      </div>
    </>
  );
};
